import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IMedia } from '../live/agent-live/model/IMedia';
import { MIHttpClientService } from '../../../shared/mi-http-client.service';
import {ContentTypesEnum, IContent} from '../../admin/content/content.model';
import {switchMap, catchError, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CmsService {

  constructor(
    private miClient: MIHttpClientService,
  ) {
  }

  public getCMSMediaList(projectId: number, accountId: number, roomId: string, endpointTypeId?: number): Observable<IMedia[]> {
    const buildParams = (includeEndpointType: boolean, includeAccountAndRoom: boolean) => {
      const params = [];
      if (includeAccountAndRoom && accountId !== null && accountId !== undefined) {
        params.push('accountId=' + encodeURIComponent(accountId));
      }
      if (includeAccountAndRoom && roomId) {
        params.push('roomId=' + roomId);
      }
      if (includeEndpointType && endpointTypeId !== null && endpointTypeId !== undefined) {
        params.push('endpointTypeId=' + endpointTypeId);
      }
      return params.length ? '?' + params.join('&') : '';
    };

    const urlWithAll = 'contentsByProject/' + projectId + buildParams(true, true);
    const urlWithoutEndpointType = 'contentsByProject/' + projectId + buildParams(false, true);
    const urlProjectOnly = 'contentsByProject/' + projectId + buildParams(false, false);

    const normalize = (items: any): IMedia[] => Array.isArray(items) ? items : (items?.list ?? []);

    return this.miClient.get<IMedia[]>(urlWithAll).pipe(
      switchMap(items => {
        const normalized = normalize(items);
        return (normalized.length === 0 && endpointTypeId !== null && endpointTypeId !== undefined)
          ? this.miClient.get<IMedia[]>(urlWithoutEndpointType)
          : of(normalized);
      }),
      switchMap(items => {
        const normalized = normalize(items);
        return normalized.length === 0
          ? this.miClient.get<IMedia[]>(urlProjectOnly)
          : of(normalized);
      }),
      map(items => normalize(items)),
      catchError(() => this.miClient.get<IMedia[]>(urlProjectOnly).pipe(map(items => normalize(items)))),
    );
  }

  getProjectContentsOfType(projectId: number, type: number) {
    const url = 'contentsByType/' + projectId + '?type=' + type;
    return this.miClient.get<IContent[] | {list?: IContent[]; items?: IContent[]}>(url).pipe(
      map(items => this.normalizeContentResponse(items)),
    );
  }

  getProjectContents(projectId: number): Observable<IContent[]> {
    const url = 'contentsByProject/' + projectId;
    return this.miClient.get<IContent[] | {list?: IContent[]; items?: IContent[]}>(url).pipe(
      map(items => this.normalizeContentResponse(items)),
    );
  }

  private normalizeContentResponse(items: IContent[] | {list?: IContent[]; items?: IContent[]} | IContent): IContent[] {
    if (Array.isArray(items)) {
      return items;
    }

    if (Array.isArray((items as {list?: IContent[]})?.list)) {
      return (items as {list: IContent[]}).list;
    }

    if (Array.isArray((items as {items?: IContent[]})?.items)) {
      return (items as {items: IContent[]}).items;
    }

    return items ? [items as IContent] : [];
  }
}
